import { useCallback, useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { useQueryClient } from "react-query";
import { fetchProductsByTitleId } from "../api/productService";
/**
 * Shared guard for every "Vytvořit produkt" action: when the title already has
 * at least one product, the user must confirm creating another one before
 * `proceed` runs. A title without products proceeds immediately.
 *
 * When the caller does not know the product count (`hasProducts` undefined),
 * the title's products are read through the shared `["products", titleId]`
 * query: a cached result (e.g. from the products panel) is reused, otherwise
 * the products are fetched once.
 */
export var useCreateProductConfirm = function (proceed) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _a = useState(null), pendingTitleId = _a[0], setPendingTitleId = _a[1];
    var decide = useCallback(function (titleId, hasProducts) {
        if (hasProducts) {
            setPendingTitleId(titleId);
        }
        else {
            proceed(titleId);
        }
    }, [proceed]);
    var requestCreate = useCallback(function (titleId, hasProducts) {
        var _a, _b;
        if (hasProducts !== undefined) {
            decide(titleId, hasProducts);
            return;
        }
        var queryKey = ["products", titleId];
        var cached = queryClient.getQueryData(queryKey);
        if (cached) {
            decide(titleId, ((_b = (_a = cached.items) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0);
            return;
        }
        queryClient
            .fetchQuery(queryKey, function () { return fetchProductsByTitleId(ky, titleId); })
            .then(function (data) { var _a, _b; return decide(titleId, ((_b = (_a = data === null || data === void 0 ? void 0 : data.items) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0); })
            // The lookup is only a convenience guard; never block creation on it.
            .catch(function () { return proceed(titleId); });
    }, [decide, ky, proceed, queryClient]);
    var modalProps = {
        open: pendingTitleId !== null,
        onConfirm: function () {
            var titleId = pendingTitleId;
            setPendingTitleId(null);
            if (titleId)
                proceed(titleId);
        },
        onCancel: function () { return setPendingTitleId(null); },
    };
    return { requestCreate: requestCreate, modalProps: modalProps };
};
